/**
 * Landing-source capture for marketing attribution (`user.referralSource`).
 *
 * referral_source — a bare localStorage STRING (not a cookie: no expiry, domain,
 * or SameSite attributes) holding the source a visitor landed with. Written once
 * per document load by the mount-only effect in AnalyticsTracker
 * (src/routes/index.tsx), sent as `referralSource` on the signup payload
 * (Onboarding/PasswordStep.tsx), and cleared after a successful registration.
 * The same key is read by utils/analytics.ts for GA4 user properties; a
 * referrer-derived source reaching GA4 is intentional (Eric's ruling,
 * 2026-09-23 — one key, one column, no analytics code changes).
 *
 * Precedence: explicit beats implicit. `?ref` wins over `?utm_source`, and the
 * referrer fallback runs only when neither is present on the landing URL.
 *
 * Touch rule: last-touch on trigger. A landing that yields a source overwrites
 * whatever was stored; a landing that yields nothing leaves it untouched.
 * The effect that calls this runs once per document load, so SPA navigation
 * never re-evaluates (and document.referrer is per-document anyway).
 */

export const REFERRAL_SOURCE_STORAGE_KEY = 'referral_source';

/**
 * Partner referrer allowlist. Adding a partner is one row. Matched on the
 * referrer's hostname by suffix (exact host or any subdomain), never by
 * substring on the raw string. The `source` value must be the exact string
 * other paths to the same partner produce (e.g. a future `?ref=topdecked`
 * export URL), so they aggregate as one source.
 */
export const REFERRER_SOURCE_MAP: ReadonlyArray<{ hostSuffix: string; source: string }> = [
  { hostSuffix: 'topdecked.com', source: 'topdecked' },
];

/**
 * Resolve a referrer URL to a partner source via REFERRER_SOURCE_MAP.
 * Empty, unparsable, or non-matching referrers return null.
 */
export const matchReferrerSource = (referrer: string): string | null => {
  if (!referrer) return null;

  let hostname: string;
  try {
    hostname = new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }

  const match = REFERRER_SOURCE_MAP.find(
    ({ hostSuffix }) => hostname === hostSuffix || hostname.endsWith(`.${hostSuffix}`),
  );
  return match ? match.source : null;
};

/**
 * Capture the landing source from the URL query string, falling back to the
 * referrer allowlist when no explicit source is present. No-ops when nothing
 * resolves. Never throws (localStorage may be unavailable).
 */
export const captureReferralSource = (search: string, referrer: string): void => {
  const params = new URLSearchParams(search);
  const source = params.get('ref') || params.get('utm_source') || matchReferrerSource(referrer);
  if (!source) return;

  try {
    localStorage.setItem(REFERRAL_SOURCE_STORAGE_KEY, source);
  } catch {
    // localStorage not available
  }
};
